import React from "react";
import { Alert, Badge, Card, Col, Row } from "react-bootstrap";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import moment from "moment";
import { formatAngka } from "../../utils";

ChartJS.register(ArcElement, Tooltip, Legend);

const URUTAN_TIER = ["T1", "T2", "T3", "T4"];

const WARNA_TIER = {
  T1: "rgba(40, 167, 69, 0.8)",
  T2: "rgba(54, 162, 235, 0.8)",
  T3: "rgba(255, 193, 7, 0.8)",
  T4: "rgba(220, 53, 69, 0.8)",
};

const opsiDoughnut = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom", labels: { font: { size: 10 } } },
  },
};

function PanelKualitas({ data }) {
  if (!data) {
    return (
      <div className="text-muted" style={{ fontSize: "12px" }}>
        Data kualitas belum tersedia.
      </div>
    );
  }

  const komposisi = data.komposisi_tier || {};
  const chartData = {
    labels: URUTAN_TIER.map(
      (t) => `${t} (${formatAngka(komposisi[t]?.persen, 1)}%)`
    ),
    datasets: [
      {
        data: URUTAN_TIER.map((t) => komposisi[t]?.jumlah || 0),
        backgroundColor: URUTAN_TIER.map((t) => WARNA_TIER[t]),
        borderWidth: 0,
      },
    ],
  };

  const ditahan = data.indikator_ditahan || [];
  const kedaluwarsa = data.baseline_mendekati_kedaluwarsa || [];
  const tanpaSumber = data.baseline_tanpa_sumber || [];
  const kelengkapan = data.kelengkapan || {};

  return (
    <Row className="g-2">
      <Col xs={12} lg={4}>
        <Card className="border-0 shadow-sm h-100">
          <Card.Body className="p-3">
            <div className="fw-bold mb-2" style={{ fontSize: "13px" }}>
              Komposisi Tier Data
            </div>
            <div style={{ height: 220 }}>
              <Doughnut data={chartData} options={opsiDoughnut} />
            </div>
            <div className="mt-3" style={{ fontSize: "11px" }}>
              <div className="fw-bold mb-1">Kelengkapan Data</div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Order dengan data produksi</span>
                <span>
                  {formatAngka(kelengkapan.order_dengan_data_produksi, 0)} /{" "}
                  {formatAngka(kelengkapan.order_periode, 0)}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Persentase</span>
                <span className="fw-bold">
                  {formatAngka(kelengkapan.persen, 1)}%
                </span>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col xs={12} lg={8}>
        <Card className="border-0 shadow-sm h-100">
          <Card.Body className="p-3">
            <div className="fw-bold mb-2" style={{ fontSize: "13px" }}>
              Indikator Ditahan Sistem
            </div>
            {/* Daftar ini adalah alasan angka tertentu tidak muncul di panel lain.
                Tanpa ditampilkan, indikator yang hilang terbaca sebagai nol. */}
            {ditahan.length > 0 ? (
              <div className="table-responsive">
                <table
                  className="table table-bordered mb-0"
                  style={{ fontSize: "11px" }}
                >
                  <thead className="table-light">
                    <tr>
                      <th>Indikator</th>
                      <th className="text-center">Tier Data</th>
                      <th className="text-center">Tier Minimum</th>
                      <th>Alasan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ditahan.map((val) => (
                      <tr key={val.code}>
                        <td>
                          {val.nama}
                          <div className="text-muted" style={{ fontSize: "10px" }}>
                            {val.code}
                          </div>
                        </td>
                        <td className="text-center">
                          <Badge bg="danger">{val.tier_data || "-"}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="secondary">{val.tier_minimum || "-"}</Badge>
                        </td>
                        <td>{val.alasan}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Alert variant="success" className="py-1 px-2 mb-0" style={{ fontSize: "11px" }}>
                Tidak ada indikator yang ditahan pada periode ini.
              </Alert>
            )}

            {kedaluwarsa.length > 0 && (
              <Alert variant="warning" className="mt-3 py-2 px-2 mb-0" style={{ fontSize: "11px" }}>
                <div className="fw-bold">Baseline mendekati kedaluwarsa</div>
                <ul className="mb-0 ps-3">
                  {kedaluwarsa.map((val, i) => (
                    <li key={i}>
                      {val.indicator_code} ({val.tipe}) &mdash; berlaku sampai{" "}
                      {val.berlaku_sampai
                        ? moment(val.berlaku_sampai).format("DD MMM YYYY")
                        : "-"}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {tanpaSumber.length > 0 && (
              <Alert variant="danger" className="mt-2 py-2 px-2 mb-0" style={{ fontSize: "11px" }}>
                <div className="fw-bold">Baseline tanpa sumber</div>
                <div>
                  {tanpaSumber.map((val) => val.indicator_code).join(", ")} &mdash;
                  klaim dari baseline ini tidak dapat dipertanggungjawabkan.
                </div>
              </Alert>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

export default PanelKualitas;
